// Live channel client — the web app's connection to the backend /ws/live bus.
//
// The app had no live socket before this (real-time UI was react-query polling);
// this opens ONE tenant-scoped WebSocket for the authenticated session and, for
// now, handles `operator.alert` events pushed by automation rules — showing an
// in-app toast, playing a sound, and/or announcing via TTS to every operator of
// the tenant. Other live event types are ignored here (future: query invalidation
// to replace polling).
//
// Mounted inside AppLayout (persistent authenticated shell, within ToastProvider
// so it can use useToast()). Renders nothing.
import { useEffect, useRef } from 'react';
import { useToast } from '@/components/ui/Toast';
import { getAccessToken } from '@/lib/api';
import { audioService } from '@/services/audio.service';
import { speakText, playCustomSound } from '@/services/sound-manager';

interface OperatorAlert {
  message?: string;
  tone?: 'info' | 'success' | 'error';
  duration?: number;
  sound?: string;
  speak?: string;
  deviceId?: string;
  incidentId?: string;
  site?: string;
}
interface LiveEvent { type: string; tenantId?: string; at?: string; payload?: OperatorAlert }

/** ws(s)://<host>/ws/live?token=… — same-origin in the browser, explicit in Tauri. */
function liveUrl(token: string): string {
  const base = (import.meta.env.VITE_WS_URL as string) || '';
  if (base) return `${base.replace(/\/$/, '')}/ws/live?token=${encodeURIComponent(token)}`;
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}/ws/live?token=${encodeURIComponent(token)}`;
}

export function LiveConnection() {
  const toast = useToast();
  // Keep the latest toast fn without re-opening the socket on every render.
  const toastRef = useRef(toast);
  toastRef.current = toast;

  useEffect(() => {
    let ws: WebSocket | null = null;
    let closed = false;
    let attempt = 0;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    // Browsers block audio until a user gesture — unlock the audio element on the
    // first interaction so rule-driven sounds can play thereafter.
    const unlock = () => { audioService.setEnabled(true); };
    window.addEventListener('pointerdown', unlock, { once: true });

    const connect = () => {
      const token = getAccessToken();
      if (!token) {
        // Not authenticated yet — retry shortly (AuthContext sets the token on login).
        reconnectTimer = setTimeout(connect, 2000);
        return;
      }
      try {
        ws = new WebSocket(liveUrl(token));
      } catch {
        scheduleReconnect();
        return;
      }
      ws.onopen = () => { attempt = 0; };
      ws.onmessage = (ev) => {
        let msg: LiveEvent;
        try { msg = JSON.parse(ev.data as string); } catch { return; }
        if (msg.type === 'operator.alert' && msg.payload) handleAlert(msg.payload);
      };
      ws.onclose = () => { if (!closed) scheduleReconnect(); };
      ws.onerror = () => { try { ws?.close(); } catch { /* noop */ } };
    };

    const scheduleReconnect = () => {
      if (closed || reconnectTimer) return;
      attempt += 1;
      const delay = Math.min(1000 * 2 ** Math.min(attempt, 5), 30000);
      reconnectTimer = setTimeout(() => { reconnectTimer = null; connect(); }, delay);
    };

    const handleAlert = (a: OperatorAlert) => {
      if (a.message) {
        toastRef.current.show({ message: a.message, tone: a.tone ?? 'info', duration: a.duration });
      }
      if (a.sound) {
        // A URL → custom sound; otherwise a catalog sound id.
        if (/^https?:\/\//i.test(a.sound)) playCustomSound(a.sound);
        else void audioService.playSound(a.sound);
      }
      if (a.speak) speakText(a.speak);
    };

    connect();
    return () => {
      closed = true;
      window.removeEventListener('pointerdown', unlock);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      try { ws?.close(); } catch { /* noop */ }
    };
  }, []);

  return null;
}
